/**
 * Chatterbox English — zero-shot voice cloning via Transformers.js / CDN.
 * Falls back to worker when available; otherwise runs on main thread.
 *
 * MIT license · onnx-community/chatterbox-ONNX
 */

import { loadTransformers } from './cdn';
import {
  CloneResult,
  LoadProgress,
  PresetVoice,
  SynthOptions,
  SynthResult,
  TtsEngine,
} from './types';
import { concatPcm, splitSentences } from '../audioUtils';
import { callTtsWorker, isWorkerAvailable, terminateTtsWorker } from './workerClient';

const REPO = 'onnx-community/chatterbox-ONNX';
let model: any = null;
let loadPromise: Promise<void> | null = null;
let abortFlag = false;
/** True when Chatterbox is loaded inside the module worker */
let workerReady = false;
/** Cached reference audio tensors keyed by a simple hash of pcm length+sum */
const refCache: { [key: string]: any } = {};

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

async function detectDevice(): Promise<string> {
  const win = getWin();
  try {
    if (win.navigator && win.navigator.gpu) {
      const adapter = await win.navigator.gpu.requestAdapter();
      if (adapter) return 'webgpu';
    }
  } catch {
    /* ignore */
  }
  return 'wasm';
}

function pcmKey(pcm: Float32Array): string {
  let sum = 0;
  const step = Math.max(1, Math.floor(pcm.length / 64));
  for (let i = 0; i < pcm.length; i += step) sum += pcm[i];
  return pcm.length + ':' + sum.toFixed(4);
}

function trimPcm(pcm: Float32Array, sampleRate: number): Float32Array {
  if (pcm.length < sampleRate * 2) {
    throw new Error('Mẫu giọng quá ngắn — hãy dùng đoạn 5–30 giây.');
  }
  if (pcm.length > sampleRate * 30) {
    return pcm.subarray(0, sampleRate * 30);
  }
  return pcm;
}

async function ensureChatterboxMain(onProgress?: (info: LoadProgress) => void): Promise<void> {
  if (model) return;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    onProgress && onProgress({ status: 'Đang tải Transformers.js…', progress: 0.02 });
    const T = await loadTransformers();
    if (T.env) {
      T.env.allowLocalModels = false;
    }

    const device = await detectDevice();
    onProgress &&
      onProgress({
        status: 'Đang tải Chatterbox (~1.5GB, lần đầu)… ' + device,
        progress: 0.05,
      });

    const progress_callback = (data: any) => {
      if (!onProgress) return;
      let progress: number | undefined;
      if (typeof data.progress === 'number') {
        const raw = data.progress > 1 ? data.progress / 100 : data.progress;
        progress = Math.max(0, Math.min(1, raw));
      } else if (data.loaded != null && data.total) {
        progress = Math.max(0, Math.min(1, data.loaded / data.total));
      }
      onProgress({
        status: 'Đang tải… ' + ((data && data.status) || ''),
        progress,
        file: data && data.file,
      });
    };

    if (T.ChatterboxModel && T.ChatterboxModel.from_pretrained) {
      model = await T.ChatterboxModel.from_pretrained(REPO, {
        dtype: device === 'webgpu' ? 'q4f16' : 'q4',
        device,
        progress_callback,
      });
    } else if (T.pipeline) {
      model = await T.pipeline('text-to-speech', REPO, {
        quantized: true,
        progress_callback,
        device,
      });
    } else {
      throw new Error(
        'Transformers.js chưa hỗ trợ Chatterbox trên CDN này. Thử Chrome mới + WebGPU, hoặc dùng Kokoro / Piper.'
      );
    }

    onProgress && onProgress({ status: 'Model sẵn sàng.', progress: 1 });
  })();

  try {
    await loadPromise;
  } catch (e) {
    loadPromise = null;
    model = null;
    throw e;
  }
}

async function ensureLoadedAnywhere(onProgress?: (info: LoadProgress) => void): Promise<'worker' | 'main'> {
  if (workerReady) return 'worker';
  if (model) return 'main';

  if (isWorkerAvailable()) {
    try {
      await callTtsWorker('load', { engine: 'chatterbox', repo: REPO }, onProgress);
      workerReady = true;
      return 'worker';
    } catch (e: any) {
      workerReady = false;
      onProgress &&
        onProgress({
          status: 'Worker lỗi, thử chạy trên main thread… ' + String((e && e.message) || e),
        });
    }
  }

  await ensureChatterboxMain(onProgress);
  return 'main';
}

async function encodeReference(
  pcm: Float32Array,
  sampleRate: number,
  onProgress?: (info: LoadProgress) => void
): Promise<any> {
  await ensureChatterboxMain(onProgress);
  const key = pcmKey(pcm);
  if (refCache[key]) return refCache[key];

  onProgress && onProgress({ status: 'Đang mã hóa giọng mẫu…' });

  if (model && typeof model.encode_speech === 'function') {
    const encoded = await model.encode_speech(pcm, sampleRate);
    refCache[key] = encoded;
    return encoded;
  }

  // Raw reference is enough for generate({ audio }) — not a fake embedding
  const encoded = { audio: pcm, sampling_rate: sampleRate, rawReference: true };
  refCache[key] = encoded;
  return encoded;
}

function extractPcm(audio: any): { pcm: Float32Array; sampleRate: number } {
  if (audio && audio.audio instanceof Float32Array) {
    return {
      pcm: audio.audio,
      sampleRate: audio.sampling_rate || audio.sample_rate || 24000,
    };
  }
  if (audio instanceof Float32Array) {
    return { pcm: audio, sampleRate: 24000 };
  }
  if (audio && audio.data) {
    return {
      pcm: new Float32Array(audio.data),
      sampleRate: audio.sampleRate || 24000,
    };
  }
  throw new Error('Không nhận được âm thanh từ Chatterbox.');
}

export const chatterboxEngine: TtsEngine = {
  id: 'chatterbox',
  supportsCloning: true,
  supportsNativeSpeed: false,
  supportsExaggeration: true,
  canExportFile: true,

  ensureLoaded: async (_modelId: string, onProgress?: (info: LoadProgress) => void) => {
    await ensureLoadedAnywhere(onProgress);
  },

  isReady: () => workerReady || !!model,

  listVoices: async (): Promise<PresetVoice[]> => {
    return [
      {
        id: 'default',
        label: 'Default (Chatterbox)',
        lang: 'en',
      },
    ];
  },

  cloneVoice: async (
    _modelId: string,
    pcmIn: Float32Array,
    sampleRate: number,
    onProgress?: (info: LoadProgress) => void
  ): Promise<CloneResult> => {
    const pcm = trimPcm(pcmIn, sampleRate);
    const where = await ensureLoadedAnywhere(onProgress);

    if (where === 'worker') {
      try {
        onProgress && onProgress({ status: 'Đang mã hóa giọng mẫu (worker)…', progress: 0.4 });
        const res = await callTtsWorker(
          'clone',
          { engine: 'chatterbox', pcm, sampleRate },
          onProgress
        );
        const embedding =
          res.embedding instanceof Float32Array
            ? res.embedding
            : res.embedding
            ? new Float32Array(res.embedding)
            : new Float32Array(0);
        return {
          embedding,
          meta: {
            referencePcm: Array.from(pcm),
            sampleRate,
            ...(res.meta || {}),
          },
          sampleRate,
        };
      } catch (e: any) {
        // Fall through to main — PCM is still intact (workerClient copies)
        onProgress &&
          onProgress({
            status: 'Worker clone lỗi, thử main thread… ' + String((e && e.message) || e),
          });
        workerReady = false;
      }
    }

    const encoded = await encodeReference(pcm, sampleRate, onProgress);
    let embedding = new Float32Array(0);
    if (encoded && encoded.speaker_embeddings instanceof Float32Array) {
      embedding = encoded.speaker_embeddings;
    } else if (encoded && encoded.embedding instanceof Float32Array) {
      embedding = encoded.embedding;
    }

    return {
      embedding,
      meta: {
        referencePcm: Array.from(pcm),
        sampleRate,
        encodedKey: pcmKey(pcm),
        rawReference: !!(encoded && encoded.rawReference),
      },
      sampleRate,
    };
  },

  synthesize: async (_modelId: string, opts: SynthOptions): Promise<SynthResult> => {
    abortFlag = false;
    const text = (opts.text || '').trim();
    if (!text) throw new Error('Hãy nhập văn bản.');

    const exaggeration =
      typeof opts.exaggeration === 'number' ? opts.exaggeration : 0.5;

    const refAudio = opts.referencePcm || null;
    const refRate = opts.referenceSampleRate || 24000;

    const where = await ensureLoadedAnywhere(opts.onProgress);

    if (where === 'worker') {
      try {
        const res = await callTtsWorker(
          'synthesize',
          {
            engine: 'chatterbox',
            text,
            exaggeration,
            referencePcm: refAudio,
            referenceSampleRate: refRate,
            embedding: opts.embedding,
          },
          opts.onProgress,
          opts.signal
        );
        return {
          pcm: res.pcm instanceof Float32Array ? res.pcm : new Float32Array(res.pcm || []),
          sampleRate: res.sampleRate || 24000,
          nativeSpeedApplied: false,
        };
      } catch (e: any) {
        if (e && String(e.message || e).indexOf('Đã hủy') >= 0) throw e;
        workerReady = false;
        opts.onProgress &&
          opts.onProgress({
            status: 'Worker synthesize lỗi, thử main thread…',
          });
      }
    }

    await ensureChatterboxMain(opts.onProgress);

    // Clone mode requires reference audio; preset Default can run without it
    if (opts.embedding || opts.referencePcm) {
      if (!refAudio || !refAudio.length) {
        throw new Error(
          'Thiếu mẫu giọng (reference audio). Hãy clone lại voice hoặc chọn giọng Default.'
        );
      }
    }

    const sentences = splitSentences(text);
    const chunks: Float32Array[] = [];
    let sampleRate = 24000;

    for (let i = 0; i < sentences.length; i++) {
      if (abortFlag || (opts.signal && opts.signal.aborted)) {
        throw new Error('Đã hủy.');
      }
      opts.onProgress &&
        opts.onProgress({
          status: 'Đang tổng hợp ' + (i + 1) + '/' + sentences.length + '…',
          progress: (i + 1) / sentences.length,
        });

      let audio: any;
      if (typeof model.generate === 'function') {
        const genOpts: any = {
          exaggeration,
          language: 'en',
        };
        if (refAudio && refAudio.length) {
          genOpts.speaker_audio = refAudio;
          genOpts.audio = refAudio;
          genOpts.sampling_rate = refRate;
        }
        audio = await model.generate(sentences[i], genOpts);
      } else if (typeof model === 'function') {
        const pipeOpts: any = {};
        if (opts.embedding) pipeOpts.speaker_embeddings = opts.embedding;
        if (refAudio && refAudio.length) pipeOpts.audio = refAudio;
        audio = await model(sentences[i], pipeOpts);
      } else {
        throw new Error('Chatterbox API không tương thích trên bản Transformers này.');
      }

      const extracted = extractPcm(audio);
      sampleRate = extracted.sampleRate;
      chunks.push(extracted.pcm);
      chunks.push(new Float32Array(Math.floor(sampleRate * 0.12)));
    }

    return {
      pcm: concatPcm(chunks),
      sampleRate,
      nativeSpeedApplied: false,
    };
  },

  cancel: () => {
    abortFlag = true;
  },

  unload: async () => {
    model = null;
    loadPromise = null;
    workerReady = false;
    Object.keys(refCache).forEach((k) => delete refCache[k]);
    try {
      terminateTtsWorker();
    } catch {
      /* ignore */
    }
  },
};
